import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import Card from '../shared/Card';
import StatusBadge from '../shared/StatusBadge';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { cn } from '../../lib/utils';
import supabase from '../../supabase/supabase';
import { useAuth } from '../../lib/auth';

const {
  FiWatch, FiActivity, FiGlobe, FiHeart, FiRadio, FiWifi,
  FiBattery, FiBatteryCharging, FiClock, FiShield, FiShieldOff,
  FiRefreshCw, FiAlertCircle, FiInfo,
} = FiIcons;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const PROVIDER_ICONS = {
  apple:       FiWatch,
  garmin:      FiActivity,
  generic_api: FiGlobe,
  fitbit:      FiHeart,
  oura:        FiRadio,
  bluetooth:   FiWifi,
  simulator:   FiActivity,
};

const statusToBadge = (status) => {
  if (status === 'active')   return 'normal';
  if (status === 'inactive') return 'info';
  if (status === 'error')    return 'critical';
  return 'info';
};

const batteryColor = (battery) => {
  if (battery === null || battery === undefined) return 'text-gray-300';
  if (battery > 50) return 'text-emerald-500';
  if (battery > 20) return 'text-amber-500';
  return 'text-rose-500';
};

const formatLastSync = (ts) => {
  if (!ts) return 'Never';
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60)   return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

// ─── Device row ───────────────────────────────────────────────────────────────
const DeviceRow = ({ device }) => {
  const Icon = PROVIDER_ICONS[device.provider] ?? FiGlobe;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-3xl bg-gray-50 border border-gray-100 hover:border-brand-200 transition-colors"
    >
      {/* Identity */}
      <div className="flex items-center gap-4 min-w-0">
        <div className="w-12 h-12 rounded-2xl bg-white shadow-sm border border-gray-100 flex items-center justify-center flex-shrink-0">
          <SafeIcon icon={Icon} className="text-xl text-gray-700" />
        </div>
        <div className="min-w-0">
          <p className="font-black text-gray-900 text-sm truncate">{device.device_name}</p>
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">
            {device.provider}{device.manufacturer ? ` · ${device.manufacturer}` : ''}
          </p>
        </div>
      </div>

      {/* Metrics */}
      <div className="flex items-center gap-6 flex-wrap sm:flex-nowrap">
        {/* Battery */}
        <div className="flex items-center gap-1.5 min-w-[64px]">
          <SafeIcon
            icon={device.battery_level > 20 ? FiBattery : FiBatteryCharging}
            className={cn('text-base', batteryColor(device.battery_level))}
          />
          <span className="text-xs font-bold text-gray-600">
            {device.battery_level !== null && device.battery_level !== undefined
              ? `${device.battery_level}%` : '—'}
          </span>
        </div>

        {/* Last sync */}
        <div className="flex items-center gap-1.5 min-w-[120px]">
          <SafeIcon icon={FiClock} className="text-sm text-gray-400" />
          <span className="text-xs font-bold text-gray-600">
            {formatLastSync(device.last_sync_at)}
          </span>
        </div>

        {/* Consent */}
        <div className="flex items-center gap-1.5 min-w-[90px]">
          <SafeIcon
            icon={device.consent_status === 'granted' ? FiShield : FiShieldOff}
            className={cn('text-sm', device.consent_status === 'granted'
              ? 'text-emerald-500' : 'text-amber-500')}
          />
          <span className="text-xs font-bold text-gray-600 capitalize">
            {device.consent_status}
          </span>
        </div>

        {/* Status */}
        <StatusBadge status={statusToBadge(device.status)} label={device.status} />
      </div>
    </motion.div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const ConnectedDevices = () => {
  const { user } = useAuth();
  const [devices, setDevices]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  const fetchDevices = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: dbError } = await supabase
        .from('devices')
        .select('*')
        .eq('patient_id', user.id)
        .order('created_at', { ascending: false });

      if (dbError) throw dbError;
      setDevices(data ?? []);
    } catch (err) {
      console.error('[ConnectedDevices] fetch error:', err);
      setError('Could not load devices. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  const activeCount = devices.filter((d) => d.status === 'active').length;

  return (
    <div className="space-y-6">
      <Card className="!p-0 overflow-hidden">
        {/* Header */}
        <div className="p-6 md:p-8 lg:p-10 pb-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-black text-gray-900">Connected Devices</h2>
            <p className="text-xs font-bold text-gray-400 mt-1">
              {loading
                ? 'Loading devices...'
                : `${activeCount} of ${devices.length} devices actively syncing`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchDevices}
              disabled={loading}
              className="w-9 h-9 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center hover:bg-gray-100 transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <SafeIcon icon={FiRefreshCw} className={`text-sm text-gray-500 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <div className="w-12 h-12 rounded-2xl bg-brand-50 flex items-center justify-center">
              <SafeIcon icon={FiRadio} className="text-xl text-brand-500" />
            </div>
          </div>
        </div>

        {/* Device list */}
        <div className="px-6 md:px-8 lg:px-10 pb-8 space-y-3">
          {loading ? (
            <div className="text-center py-10">
              <SafeIcon icon={FiRefreshCw} className="text-3xl text-gray-200 mx-auto mb-3 animate-spin" />
              <p className="text-sm font-bold text-gray-400">Loading devices...</p>
            </div>
          ) : error ? (
            <div className="flex items-start gap-2.5 bg-rose-50 border border-rose-100 rounded-2xl p-4">
              <SafeIcon icon={FiAlertCircle} className="text-rose-500 text-sm flex-shrink-0 mt-0.5" />
              <p className="text-xs font-medium text-rose-700">{error}</p>
            </div>
          ) : devices.length > 0 ? (
            devices.map((device) => <DeviceRow key={device.id} device={device} />)
          ) : (
            <div className="text-center py-12">
              <SafeIcon icon={FiWifi} className="text-4xl text-gray-200 mx-auto mb-3" />
              <p className="text-sm font-bold text-gray-400">No devices registered yet</p>
              <p className="text-xs text-gray-400 mt-1">
                Devices will appear here once registered through the platform.
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Pending providers */}
      <Card>
        <div className="flex items-center gap-3 mb-4">
          <SafeIcon icon={FiHeart} className="text-rose-400" />
          <h3 className="font-black text-gray-900 text-sm uppercase tracking-widest">
            Other Supported Providers
          </h3>
        </div>
        <p className="text-xs font-medium text-gray-500 leading-relaxed mb-4">
          The following providers are recognised by the platform but not yet available
          for connection on your account.
        </p>
        <div className="flex flex-wrap gap-2">
          {['Fitbit', 'Oura', 'Bluetooth'].map((name) => (
            <span
              key={name}
              className="px-3 py-1.5 rounded-xl bg-gray-50 border border-gray-100 text-[10px] font-black uppercase tracking-widest text-gray-400"
            >
              {name} · Pending
            </span>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default ConnectedDevices;